import { FormEvent, useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AuthApiError } from "../services/auth";

interface PasswordChangePromptProps {
  title: string;
  description: string;
  submitLabel?: string;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  onSubmitPassword?: (password: string) => Promise<unknown>;
  onSuccess?: () => void;
  className?: string;
}

export function PasswordChangePrompt({
  title,
  description,
  submitLabel = "Passwort speichern",
  secondaryActionLabel,
  onSecondaryAction,
  onSubmitPassword,
  onSuccess,
  className = "",
}: PasswordChangePromptProps) {
  const { changePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Bitte wählen Sie ein Passwort mit mindestens 8 Zeichen.");
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Die beiden Passwörter stimmen nicht überein.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (onSubmitPassword) {
        await onSubmitPassword(password);
      } else {
        await changePassword(password);
      }
      setPassword("");
      setPasswordConfirmation("");
      onSuccess?.();
    } catch (submitError) {
      setError(
        submitError instanceof AuthApiError
          ? submitError.message
          : submitError instanceof Error && submitError.message
            ? submitError.message
            : "Das Passwort konnte nicht gespeichert werden",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className}>
      <div className="mb-5">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">{description}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
            Neues Passwort
          </label>
          <div className="relative mt-2">
            <Lock
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              id="new-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="new-password"
              placeholder="Mindestens 8 Zeichen"
              className="w-full rounded-lg border border-gray-300 py-3 pl-12 pr-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="new-password-confirmation"
            className="block text-sm font-medium text-gray-700"
          >
            Passwort wiederholen
          </label>
          <div className="relative mt-2">
            <Lock
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              id="new-password-confirmation"
              type={showPasswordConfirmation ? "text" : "password"}
              value={passwordConfirmation}
              onChange={(event) => setPasswordConfirmation(event.target.value)}
              required
              autoComplete="new-password"
              placeholder="Passwort erneut eingeben"
              className="w-full rounded-lg border border-gray-300 py-3 pl-12 pr-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black"
            />
            <button
              type="button"
              onClick={() => setShowPasswordConfirmation((current) => !current)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={
                showPasswordConfirmation ? "Passwort verbergen" : "Passwort anzeigen"
              }
            >
              {showPasswordConfirmation ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center rounded-lg bg-black px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Passwort wird gespeichert..." : submitLabel}
          </button>

          {onSecondaryAction && secondaryActionLabel ? (
            <button
              type="button"
              onClick={onSecondaryAction}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-5 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {secondaryActionLabel}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
